export const buildValidationRules = (validations = [], is_required, reg_key, label = "") => {
  if (!Array.isArray(validations)) {
    return rules;
  }
  const rules = {};

  for (const validation of validations) {
    switch (validation?.key) {
      case "required":
        if (is_required === 0) {
          break;
        } else {
          rules.required =
            validation.message
              ?.replaceAll(/<[^>]*>/g, "")
              ?.replaceAll("*", "")
              ?.trim() ||
            `${label} is required`;
          break;
        }
      case "min":
      case "minlength":
        rules.minLength = {
          value: validation.length,
          message: validation.message,
        };
        break;
      case "max":
      case "maxlength":
        rules.maxLength = {
          value: validation.length,
          message: validation.message,
        };
        break;
      case "pattern":
        rules.pattern = {
          value: new RegExp(validation?.regex?.slice(1, -1)),
          message: validation.message,
        };
        break;
      case "maxCount":
        rules.validate = (value) => {
          if (Array.isArray(value) && value?.length > validation?.length) {
            return validation?.message;
          }
          return true;
        };
        break;
      case "size":
        rules.validate = {
          ...rules.validate,
          fileSize: (files) =>
            files?.length > 0
              ? files[0]?.size < validation?.length * 1024 ||
              validation?.message ||
              "File size exceeds the allowed limit"
              : true,
        };
        break;
      case "mimes": {
        const allowedMimeTypes =
          reg_key === "resume" || reg_key === "document"
            ? validation?.types?.map((ext) => `application/${ext}`)
            : validation?.types?.map((ext) =>
              ext === "jpg" ? "image/jpeg" : `image/${ext}`
            );
        rules.validate = {
          ...rules.validate,
          fileType: (files) => {
            if (files?.length > 0) {
              return (
                allowedMimeTypes.includes(files[0].type) ||
                validation.message ||
                "Invalid file type"
              );
            }
            return true;
          },
        };
        break;
      }
      default:
        break;
    }
  }
  return rules;
};

export const normalizeStudentSignup3Payload = ({
  data,
  roleId,
  dob,
  email_verify_string,
  selectedResume,
}) => {
  let payload = { ...data };

  payload.role_id = roleId;
  payload.step_number = "step_4";
  payload.type = "register";
  payload.role_type = roleId;
  payload.email_verify_string = email_verify_string;

  if (payload.subarea) {
    payload.subareas = payload.subarea;
  }

  if (payload.mainArea) {
    payload.domains = payload.mainArea;
  }

  payload = normalizeBooleanFields(payload);
  payload = normalizeDateFields(payload, dob);
  payload = cleanupPayloadFields(payload);

  return handleResumeInPayload(payload, selectedResume);
};

const normalizeBooleanFields = (payload) => {
  const booleanKeys = [
    "mn_open_to_talk",
    "is_privacy",
    "is_terms",
    "my_consent",
    "mn_loking_for_coach",
    "mf_mental_health",
    "mf_certified_coach",
    "lgbtq_discussion",
    "is_disclaimer",
    "training_video",
  ];

  const newPayload = { ...payload };
  for (const key of booleanKeys) {
    if (key in newPayload) {
      newPayload[key] = newPayload[key] ? 1 : 0;
    }
  }
  return newPayload;
};

const normalizeDateFields = (payload, dob) => {
  const newPayload = { ...payload };
  if ("dob" in newPayload) {
    newPayload.dob = newPayload.dob ? dob : "";
  }

  if (newPayload.availability) {
    newPayload.availability = newPayload.availability ? 2 : 1;
  } else {
    newPayload.availability = 1;
    newPayload.from_date = null;
    newPayload.to_date = null;
  }

  // Handle board_roles repeater field - convert array of objects to JSON string
  if (newPayload.board_roles && Array.isArray(newPayload.board_roles)) {
    newPayload.board_roles = JSON.stringify(newPayload.board_roles);
  }

  return newPayload;
};

const cleanupPayloadFields = (payload) => {
  const newPayload = { ...payload };

  if (
    newPayload.mainArea === "undefined" ||
    newPayload.mainArea === "null" ||
    !newPayload.mainArea
  ) {
    delete newPayload.mainArea;
  }

  if (
    !newPayload.domains ||
    newPayload.domains === "undefined" ||
    newPayload.domains === "null" ||
    newPayload.domains === null
  ) {
    delete newPayload.domains;
  }

  if (
    newPayload.language === "undefined" ||
    newPayload.language === "null" ||
    !newPayload.language
  ) {
    delete newPayload.language;
  }

  return newPayload;
};

const handleResumeInPayload = (payload, selectedResume) => {
  const newPayload = { ...payload };
  if (
    selectedResume &&
    selectedResume instanceof File &&
    selectedResume.name &&
    selectedResume.size > 0 &&
    selectedResume.type === "application/pdf"
  ) {
    newPayload.resume = selectedResume;
  } else if ("resume" in newPayload) {
    const { resume, ...rest } = newPayload;
    return rest;
  }
  return newPayload;
};

const handleImageUpload = (formData, payload, selectedFile) => {
  if (payload.image instanceof File) {
    formData.append("image", payload.image);
  } else if (selectedFile instanceof File) {
    formData.append("image", selectedFile);
  }

  if ("image" in payload && !(selectedFile instanceof File)) {
    delete payload.image;
  }
};

const buildMergedKeys = (allowedKeys) => {
  let mergedKeys = [
    ...allowedKeys,
    "step_number",
    "type",
    "role_type",
    "email_verify_string",
  ];

  if (allowedKeys.includes("domains")) {
    mergedKeys.push("subareas", "subarea", "mainArea");
  }

  if (allowedKeys.includes("availability")) {
    mergedKeys.push("from_date", "to_date");
  }

  const hasBoardRoleFields =
    allowedKeys.includes("rep_company_name") &&
    allowedKeys.includes("rep_board_title") &&
    allowedKeys.includes("rep_start_date") &&
    allowedKeys.includes("rep_end_date");

  if (hasBoardRoleFields) {
    mergedKeys.push("board_roles");
  }

  return mergedKeys;
};

const normalizeBoardArrayFields = (payload) => {
  const boardArrayFields = [
    "board_entity_type",
    "board_entity_type_2",
    "course_completed",
    "accredited_director",
    "indicate_board_experience_level",
    "past_board_roles",
  ];

  for (const fieldKey of boardArrayFields) {
    if (Array.isArray(payload[fieldKey])) {
      payload[fieldKey] = payload[fieldKey].join(",");
    }
  }
};

const isValidValue = (value) => {
  return value !== undefined && value !== null && value !== "" && value !== "undefined";
};

const appendFormData = (formData, keys, payload) => {
  for (const key of keys) {
    const value = payload[key];

    if (!isValidValue(value)) continue;

    if (key === "board_roles" && Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value);
    }
  }
};

export const buildStudentSignup3FormData = ({
  data,
  registerStepFields,
  selectedFile,
}) => {
  const formData = new FormData();
  const payload = { ...data };

  handleImageUpload(formData, payload, selectedFile);

  const registerStep = registerStepFields[0]?.reg_feilds || [];
  const allowedKeys = registerStep.map((field) => field.reg_key);

  const mergedKeys = buildMergedKeys(allowedKeys);

  normalizeBoardArrayFields(payload);

  appendFormData(formData, mergedKeys, payload);

  return formData;
};